import { GetUsersDto } from '@/api/Identity/Types/GetUsersDto'
import { AuditLog } from 'api/Clients/Types/AuditLog'
import { NameDto } from 'api/Common/Types'
import {
  CreateUserRequest,
  UpdateUserAutoAssignment,
  UpdateUserRequest,
} from 'api/Identity/Types/AddEditUser'
import { AddEditTeam } from 'api/Identity/Types/AddUpdateTeam'
import { ChangePassword } from 'api/Identity/Types/ChangePassword'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { DefinedFilter } from 'api/Identity/Types/DefinedFilter'
import { DepartmentDto } from 'api/Identity/Types/DepartmentDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RegionDto } from 'api/Identity/Types/Region'
import { RoleDto } from 'api/Identity/Types/Role'
import { SessionDto } from 'api/Identity/Types/SessionDto'
import { SignInResponse } from 'api/Identity/Types/SignInResponse'
import { TeamDto } from 'api/Identity/Types/Team'
import { UpdateUserProfile } from 'api/Identity/Types/UpdateUserProfile'
import { UpdateUserTeamDto } from 'api/Identity/Types/UpdateUserTeamDto'
import { UpsertUserUserTeams } from 'api/Identity/Types/UpsertUserUserTeams'
import { User } from 'api/Identity/Types/User'
import { UserActivityDto } from 'api/Identity/Types/UserActivityDto'
import { UserFilterType } from 'api/Identity/Types/UserFilterType'
import { SimpleUserResponse, UserResponse } from 'api/Identity/Types/UserResponse'
import { UserStatisticsDto } from 'api/Identity/Types/UserStatisticsDto'
import { UserStatusDto } from 'api/Identity/Types/UserStatus'
import { UserTeamDto } from 'api/Identity/Types/UserTeamDto'
import { PageData } from 'api/Pagination/Types'
import { AxiosResponse } from 'axios'
import omitEmpty from 'omit-empty'

import { TwoFactorAuthType } from '../../api/Clients/Types/ClientDetailedDto'
import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'

export class IdentityApiClient {
  constructor(private apiClient: ApiClient) {}

  signIn: (email: string, password: string) => Promise<SignInResponse> = (email, password) => {
    return this.apiClient.post(`identity/sign-in`, { email, password })
  }
  signInActiveDirectory: (token: string) => Promise<SignInResponse> = (token) => {
    return this.apiClient.fetchAndParse(`identity/sign-in/active-directory`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'AD-Token': token,
      },
      data: {},
    })
  }
  getUserBySession: () => Promise<SignInResponse> = () => {
    return this.apiClient.get(`identity/me`)
  }
  signOut: (sessionId?: string) => Promise<SignInResponse> = (sessionId) => {
    const data = sessionId ? { sessionId } : {}
    return this.apiClient.post(`identity/sign-out`, data)
  }
  requestPasswordReset: (data: { email: string }) => Promise<void> = (data) => {
    return this.apiClient.post(`identity/password-reset/start`, data)
  }
  completeRequestResetPassword: (data: {
    email: string
    token: string
    password: string
  }) => Promise<void> = (data) => {
    return this.apiClient.fetchAndParse(`identity/password-reset/complete`, {
      method: 'POST',
      data,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  }
  getUserActiveSessions: () => Promise<PageData<SessionDto[]>> = () => {
    return this.apiClient.get(`identity/me/sessions`)
  }
  updateProfile: (data: UpdateUserProfile) => Promise<void> = (data) => {
    return this.apiClient.put(`identity/me`, omitEmpty(data))
  }
  updateProfilePassword: (data: ChangePassword) => Promise<void> = (data) => {
    return this.apiClient.put(`identity/me/password`, data)
  }
  getAuthUserPermission: () => Promise<PermissionDto[]> = () => {
    return this.apiClient.get(`identity/me/permissions`)
  }
  getDetailedAdministrationUsers: (query?: PageQuery) => Promise<PageData<UserResponse[]>> = (
    query
  ) => {
    return this.apiClient.get(`identity/administration/users?${preparePageQueryParams(query)}`)
  }
  updateUser: (
    userId: string,
    data: UpdateUserRequest,
    overrideRegionCheck: boolean
  ) => Promise<void> = (userId, data, overrideRegionCheck) => {
    return this.apiClient.put(
      `identity/administration/users/${userId}/?overrideRegionCheck=${overrideRegionCheck}`,
      data
    )
  }
  updateUserAutoAssignment: (userId: string, data: UpdateUserAutoAssignment) => Promise<void> = (
    userId,
    data
  ) => {
    return this.apiClient.put(
      `identity/administration/users/${userId}/set-paused-autoassignment`,
      data
    )
  }
  addUser: (data: CreateUserRequest) => Promise<void> = (data) => {
    return this.apiClient.post(`identity/administration/users`, data)
  }
  fetchPermissions: () => Promise<PermissionDto[]> = () => {
    return this.apiClient.get(`identity/administration/permissions`)
  }
  getTickmillCompanies: () => Promise<TickmillCompanyDto[]> = () => {
    return this.apiClient.get(`identity/tickmill-companies`)
  }
  getMyTickmillCompanies: () => Promise<TickmillCompanyDto[]> = () => {
    return this.apiClient.get(`identity/my-tickmill-companies`)
  }
  fetchRegions: () => Promise<RegionDto[]> = () => {
    return this.apiClient.get(`identity/regions`)
  }
  fetchDepartments: () => Promise<DepartmentDto[]> = () => {
    return this.apiClient.get(`identity/departments`)
  }
  fetchUserStatuses: () => Promise<UserStatusDto[]> = () => {
    return this.apiClient.get(`identity/user-statuses`)
  }
  fetchUserKinds: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`identity/user-kinds`)
  }
  fetchDateFormat: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`identity/date-format-types`)
  }
  fetchCountries: () => Promise<CountryDto[]> = () => {
    return this.apiClient.get(`identity/countries`)
  }
  fetchRoles: () => Promise<RoleDto[]> = () => {
    return this.apiClient.get(`identity/administration/roles`)
  }
  addRole: (name: string, permissionIds: number[]) => Promise<void> = (name, permissionIds) => {
    return this.apiClient.post(`identity/administration/roles`, { name, permissionIds })
  }
  updateRole: (id: string, name: string, permissionIds: number[]) => Promise<void> = (
    id,
    name,
    permissionIds
  ) => {
    return this.apiClient.put(`identity/administration/roles/${id}`, { id, name, permissionIds })
  }
  deleteRole: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`identity/administration/roles/${id}`)
  }
  getRoleEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`identity/administration/roles/${id}/events`)
  }
  fetchTeams: () => Promise<TeamDto[]> = () => {
    return this.apiClient.get(`identity/administration/teams`)
  }
  getTeamEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`identity/administration/teams/${id}/events`)
  }
  fetchTeamsSimpleModel: () => Promise<TeamDto[]> = () => {
    return this.apiClient.get(`identity/teams`)
  }
  addTeam: (data: AddEditTeam) => Promise<void> = (data) => {
    return this.apiClient.post(`identity/administration/teams`, data)
  }
  updateTeam: (id: string, data: AddEditTeam) => Promise<void> = (id, data) => {
    return this.apiClient.put(`identity/administration/teams/${id}`, data)
  }
  deleteTeam: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`identity/administration/teams/${id}`)
  }
  fetchFilterType: () => Promise<UserFilterType[]> = () => {
    return this.apiClient.get(`identity/filters-types`)
  }
  fetchFilters: () => Promise<DefinedFilter[]> = () => {
    return this.apiClient.get(`identity/filters`)
  }
  saveFilter: (data: CreateFilter) => Promise<AxiosResponse> = (data) => {
    return this.apiClient.fetch<AxiosResponse>(`identity/filters`, { data, method: 'POST' })
  }
  fetchFilterById: (id: string) => Promise<DefinedFilter> = (id) => {
    return this.apiClient.get(`identity/filters/${id}`)
  }
  updateFilterById: (filterId: string, data: CreateFilter) => Promise<DefinedFilter> = (
    filterId,
    data
  ) => {
    return this.apiClient.put(`identity/filters/${filterId}`, data)
  }
  removeFilter: (filterId: string) => Promise<void> = (filterId) => {
    return this.apiClient.delete(`identity/filters/${filterId}`)
  }
  getUsers(query?: GetUsersDto): Promise<SimpleUserResponse[]> {
    const params = new URLSearchParams()

    query?.departments?.forEach((department) => {
      params.append('departments', department.id.toString())
    })

    if (query?.kindId !== undefined) {
      params.set('kindId', query.kindId.toString())
    }

    if (query?.statusId !== undefined) {
      params.set('statusId', query.statusId.toString())
    }

    if (query?.roleName) {
      params.set('roleName', query.roleName)
    }

    return this.apiClient.get(`identity/users?${params.toString()}`)
  }
  getUserData: (userId: string) => Promise<User> = (userId) => {
    return this.apiClient.get(`identity/administration/users/${userId}`)
  }
  addUserTeams: (
    clientId: string,
    userTeams: UpsertUserUserTeams,
    overrideRegionCheck: boolean
  ) => Promise<void> = (clientId, userTeams, overrideRegionCheck) => {
    return this.apiClient.put(
      `identity/administration/users/${clientId}/user-teams?overrideRegionCheck=${overrideRegionCheck}`,
      userTeams
    )
  }
  getUserTeams: (query?: PageQuery) => Promise<PageData<UserTeamDto[]>> = (query) => {
    return this.apiClient.get(`identity/administration/user-teams?${preparePageQueryParams(query)}`)
  }
  updateUserTeam: (userTeam: UpdateUserTeamDto) => Promise<void> = (userTeam) => {
    return this.apiClient.put(`identity/administration/user-teams/${userTeam.id}`, userTeam)
  }
  deleteUserTeam: (userTeamId: string) => Promise<void> = (userTeamId) => {
    return this.apiClient.delete(`identity/administration/user-teams/${userTeamId}`)
  }
  getUserEvents: (userId: string) => Promise<AuditLog[]> = (userId) => {
    return this.apiClient.get(`identity/administration/users/${userId}/events`)
  }
  resetClientFailedLogins: (userId: string) => Promise<AuditLog[]> = (userId) => {
    return this.apiClient.put(`identity/administration/users/${userId}/unlock`, {})
  }
  resetClientForgotPasswordAttempts: (userId: string) => Promise<AuditLog[]> = (userId) => {
    return this.apiClient.put(
      `identity/administration/users/${userId}/reset-password-reset-attempts`,
      {}
    )
  }
  getAdministrationUserStatistics: (userId: string) => Promise<UserStatisticsDto> = (userId) => {
    return this.apiClient.fetchAndParse(`identity/administration/users/${userId}/user-statistics`, {
      method: 'GET',
      data: {
        skipError: true,
      },
    })
  }
  getAdministrationUserActivity: (userId: string) => Promise<PageData<UserActivityDto[]>> = (
    userId
  ) => {
    return this.apiClient.get(`identity/administration/users/activity?Search=User == ${userId}`)
  }
  getClient2FASettings: (
    id: string
  ) => Promise<{ clientId: string; twoFactorAuthTypes: TwoFactorAuthType[] }> = (id) => {
    return this.apiClient.get(`identity/2fa/${id}/security-settings`)
  }
  updateClient2FASettings: (
    clientId: string,
    values: { authMedium: string; id: number }[]
  ) => Promise<void> = (clientId, values) => {
    return this.apiClient.put(`identity/2fa/${clientId}/security-settings`, {
      twoFactorAuthTypes: values,
    })
  }
  forceLogoutClientSessions: (userId: string) => Promise<string> = (userId) => {
    return this.apiClient.post(`identity/force-sign-out-from-all-devices`, { userId })
  }
}
